import { useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { AGENT_ROLES } from 'constants/index';
import { QUERY_KEYS } from 'constants/queryKeys';
import { agentsServices } from '@services';
import { getFullName, getShortName, showError } from '@utils';

const processAgentsData = (data = []) => {
  if (!data) return { drivers: [], assignees: [] };

  const sortedData = [...data].sort((a, b) =>
    (a?.firstName || '').localeCompare(b?.firstName || '')
  );

  const drivers = [];
  const assignees = [];

  sortedData.forEach((item) => {
    // Process drivers
    if (item?.role === AGENT_ROLES.isDriver && item?.isActive) {
      drivers.push({
        isActive: item?.isActive,
        value: item?.email,
        email: item?.email,
        id: item?.email,
        label: getFullName(item) || item?.email,
        display: getFullName(item) || item?.email,
        text: getFullName(item) || item?.email,
        shortName: getShortName(item),
      });
    }

    // Process active assignees
    if (item?.isActive) {
      assignees.push({
        isActive: item?.isActive,
        value: item?.email,
        id: item?.email,
        firstName: item?.firstName,
        lastName: item?.lastName,
        email: item?.email,
        role: item?.role,
        display: getFullName(item) || item?.email,
        text: getFullName(item) || item?.email,
        label: getFullName(item) || item?.email,
        shortName: getShortName(item),
        permissions: item?.permissions,
        emailSignature: item?.emailSignature,
      });
    }
  });

  const assigneesMap = assignees.reduce((acc, assignee) => {
    if (assignee?.email) acc[assignee.email] = assignee;
    return acc;
  }, {});

  const assigneesWithNone = [
    {
      value: 'none',
      label: 'None',
      text: 'None',
      display: 'None',
      shortName: 'None',
    },
    ...assignees,
  ];

  return { drivers, assignees, assigneesWithNone, assigneesMap };
};

export const useAssignees = (options = {}) => {
  const queryClient = useQueryClient();

  const queryResult = useQuery(
    QUERY_KEYS.GET_AGENTS_KEY,
    () =>
      agentsServices.getAgents({
        page: 1,
        pageSize: 3000,
      }),
    {
      // Increased stale time to 30 minutes to reduce unnecessary rerenders across pages using Assignees, even when no network refetch is triggered.
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      ...options,
    }
  );

  const { drivers, assignees, assigneesWithNone, assigneesMap } =
    useMemo(() => {
      return processAgentsData(queryResult?.data?.data);
    }, [queryResult?.data?.data]);

  const invalidateAssignees = () => {
    queryClient.invalidateQueries(QUERY_KEYS.GET_AGENTS_KEY);
  };

  const getAssigneeName = useCallback(
    (email) => {
      if (!email) return 'None';

      const assignee = assigneesMap[email];

      if (assignee) return getFullName(assignee);
      return email;
    },
    [assigneesMap]
  );

  useEffect(() => {
    if (queryResult?.isError) {
      showError(
        queryResult?.error ||
          'Error fetching assignees. Try refreshing the page.'
      );
    }
  }, [queryResult?.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    drivers,
    assignees,
    assigneesWithNone,
    assigneesMap,
    ...queryResult,
    invalidateAssignees,
    getAssigneeName,
  };
};

export default useAssignees;
